import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Upload, Button, Space } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import PDFFieldMapper from "../../../../components/companyforms/PDFFieldMapper";

const FormModal = ({ visible, onCancel, onSubmit, initialValues, loading }) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [fileBase64, setFileBase64] = useState(null);
  const [pdfMapperVisible, setPdfMapperVisible] = useState(false);
  const [fileList, setFileList] = useState([]);

  // Reset form when initialValues changes
  useEffect(() => {
    form.resetFields();
    setFileBase64(null);
    setFileList([]); // Reset fileList

    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        config: initialValues.config
          ? JSON.stringify(initialValues.config)
          : "",
      });
      setFileBase64(initialValues.content || null);

      // Set fileList if we have content in edit mode
      if (initialValues.content) {
        setFileList([
          {
            uid: "-1",
            name: "Current PDF",
            status: "done",
            type: "application/pdf",
          },
        ]);
      }
    }
  }, [initialValues, form]);

  const handleCancel = () => {
    form.resetFields();
    setFileBase64(null);
    onCancel();
  };

  const entities = [
    {
      value: "patient",
      label: intl.formatMessage({ id: "app.settings.forms.patientLabel" }),
    },
    {
      value: "employee",
      label: intl.formatMessage({ id: "app.settings.forms.employeeLabel" }),
    },
    {
      value: "company",
      label: intl.formatMessage({ id: "app.settings.forms.companyLabel" }),
    },
  ];

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:application/pdf;base64, prefix
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onSubmit({
        ...values,
        content: fileBase64,
        config: values.config ? JSON.parse(values.config) : {},
      });
    });
  };

  const handlePDFFieldMapperSave = (config) => {
    form.setFieldValue("config", JSON.stringify(config, null, 2));
    setPdfMapperVisible(false); // Close mapper after save
  };

  const handleFileUpload = async (file) => {
    const base64 = await convertFileToBase64(file);
    setFileBase64(base64);
    setFileList([
      {
        uid: file.uid,
        name: file.name,
        status: "done",
        type: file.type,
      },
    ]);
    return false; // Prevent actual upload
  };

  // Add handler for file removal
  const handleFileRemove = () => {
    setFileBase64(null);
    setFileList([]);
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: initialValues
          ? "app.settings.forms.editFormTitle"
          : "app.settings.forms.createFormTitle",
      })}
      visible={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnClose={true} // Add this to ensure modal content is destroyed when closed
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false} // Add this to prevent form data preservation
        initialValues={{}} // Set empty initial values
      >
        <Form.Item
          name="name"
          label={intl.formatMessage({ id: "app.settings.forms.name" })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: "app.settings.forms.nameRequired",
              }),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={intl.formatMessage({ id: "app.settings.forms.description" })}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="entities"
          label={intl.formatMessage({ id: "app.settings.forms.entities" })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: "app.settings.forms.entitiesRequired",
              }),
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={intl.formatMessage({
              id: "app.settings.forms.selectEntities",
            })}
            options={entities}
          />
        </Form.Item>

        <Form.Item
          label={intl.formatMessage({ id: "app.settings.forms.formFile" })}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Upload
              beforeUpload={handleFileUpload}
              accept=".pdf"
              maxCount={1}
              fileList={fileList}
              onRemove={handleFileRemove}
            >
              <Button icon={<UploadOutlined />}>
                {intl.formatMessage({ id: "app.settings.forms.selectFile" })}
              </Button>
            </Upload>
            {fileBase64 && (
              <Button type="primary" onClick={() => setPdfMapperVisible(true)}>
                {intl.formatMessage({
                  id: "app.settings.forms.configure_pdf_fields",
                })}
              </Button>
            )}
          </Space>
        </Form.Item>

        <Form.Item
          name="config"
          label={intl.formatMessage({ id: "app.settings.forms.config" })}
        >
          <Input.TextArea
            rows={4}
            placeholder="{}"
            onBlur={(e) => {
              try {
                if (e.target.value) {
                  JSON.parse(e.target.value);
                }
              } catch (err) {
                form.setFields([
                  {
                    name: "config",
                    errors: [
                      intl.formatMessage({
                        id: "app.settings.forms.invalidJson",
                      }),
                    ],
                  },
                ]);
              }
            }}
          />
        </Form.Item>
      </Form>

      {/* Render PDFFieldMapper in a separate Modal */}
      {pdfMapperVisible && (
        <Modal
          title={intl.formatMessage({
            id: "app.settings.forms.pdf_field_mapper",
          })}
          open={pdfMapperVisible}
          onCancel={() => setPdfMapperVisible(false)}
          width={1200}
          footer={null}
        >
          <PDFFieldMapper
            initialConfig={
              form.getFieldValue("config")
                ? JSON.parse(form.getFieldValue("config"))
                : null
            }
            onSave={handlePDFFieldMapperSave}
            base64Content={fileBase64}
            buttonText={intl.formatMessage({
              id: "app.settings.forms.configure_pdf_fields",
            })}
            isFormContext={true}
          />
        </Modal>
      )}
    </Modal>
  );
};

export default FormModal;
